import React from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import multiMonthPlugin from '@fullcalendar/multimonth';
import interactionPlugin from '@fullcalendar/interaction';
import daLocale from '@fullcalendar/core/locales/da';
import { EventClickArg, EventContentArg } from '@fullcalendar/core';
import { Order, OrderStatusEnum } from '../lib/types';
import { Tooltip } from 'antd';
import './orders-calendar.css';
import { useTranslation } from 'react-i18next';

interface OrdersCalendarProps {
  orders: Order[];
  onOrderClick?: (orderId: string) => void;
}

export function OrdersCalendar({ orders, onOrderClick }: OrdersCalendarProps) {
  const { t } = useTranslation();

  // Convert orders to calendar events
  const events = React.useMemo(() => {
    return orders
      .filter((order) => order.deliveryDate) // Filter out orders without delivery dates
      .map((order) => ({
        id: order.id,
        title: `${order.companyName} - ${getProductsText(order.products)}`,
        start: order.deliveryDate,
        backgroundColor: getStatusColor(order.status),
        borderColor: getStatusColor(order.status),
        extendedProps: { order },
      }));
  }, [orders]);

  function getProductsText(products: Order['products']) {
    if (products.length === 0) return 'No products';
    if (products.length === 1) return products[0].name;
    return `${products[0].name} +${products.length - 1}`;
  }

  function getStatusColor(status: OrderStatusEnum): string {
    switch (status) {
      case OrderStatusEnum.AWAITING:
        return '#f59e0b'; // orange
      case OrderStatusEnum.PROCESSING:
        return '#3b82f6'; // blue
      case OrderStatusEnum.READY_FOR_SHIPPING:
        return '#8b5cf6'; // purple
      case OrderStatusEnum.IN_TRANSIT:
        return '#8b5cf6'; // purple
      case OrderStatusEnum.COMPLETED:
        return '#10b981'; // green
      case OrderStatusEnum.VOIDED:
        return '#ef4444'; // red
      default:
        return '#94a3b8'; // gray
    }
  }

  function handleEventClick(clickInfo: EventClickArg) {
    if (onOrderClick) {
      onOrderClick(clickInfo.event.id);
    }
  }

  function renderEventContent(eventInfo: EventContentArg) {
    const order = eventInfo.event.extendedProps.order as Order;
    const statusColor = getStatusColor(order.status);

    return (
      <Tooltip title={`${order.companyName} - ${getProductsText(order.products)}`}>
        <div
          style={{
            backgroundColor: statusColor,
            width: '100%',
            padding: '2px 4px',
            borderRadius: 4,
            color: '#ffffff',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          <span>{eventInfo.event.title}</span>
        </div>
      </Tooltip>
    );
  }

  return (
    <div className="orders-calendar-container mt-6">
      <h2 className="text-xl font-semibold mb-4">Ordre kalender</h2>
      <p className="text-sm text-muted-foreground mb-4">
        Kalenderen viser ønskede ordre leveringsdatoer
      </p>
      <FullCalendar
        plugins={[multiMonthPlugin, dayGridPlugin, timeGridPlugin, interactionPlugin]}
        headerToolbar={{
          left: 'title',
          center: '',
          right: 'today dayGridMonth,timeGridWeek prev,next',
        }}
        locale={daLocale}
        initialView="dayGridMonth"
        editable={false}
        selectable={false}
        events={events}
        weekNumbers
        firstDay={1}
        dayMaxEvents={true}
        eventContent={renderEventContent}
        eventClick={handleEventClick}
      />
    </div>
  );
}
